import React, { useEffect, useState } from "react";
import {
  FaCheck,
  FaCheckCircle,
  FaEye,
  FaMoneyBillWave,
  FaSearch,
  FaTimes,
} from "react-icons/fa";
import CompactActionBar from "../components/CompactActionBar";
import ConfirmDialog from "../components/ConfirmDialog";
import "../css/standard-components.css";
import { Income, incomeService } from "../services/incomeService";
import { projectService } from "../services/projectService";
import { formatCurrency } from "../utils/formatters";

const IncomeAuthorize: React.FC = () => {
  const [incomes, setIncomes] = useState<Income[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedIncomes, setSelectedIncomes] = useState<number[]>([]);
  const [showApproveDialog, setShowApproveDialog] = useState(false);
  const [showSingleApproveDialog, setShowSingleApproveDialog] = useState(false);
  const [incomeToApprove, setIncomeToApprove] = useState<Income | null>(null);
  const [showRejectDialog, setShowRejectDialog] = useState(false);
  const [incomeToReject, setIncomeToReject] = useState<Income | null>(null);
  const [rejectionReason, setRejectionReason] = useState("");
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedIncome, setSelectedIncome] = useState<Income | null>(null);

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const limit = 20;

  // Filter toggle
  const [showFilters, setShowFilters] = useState(false);
  // Show future-dated cheques too (default: hide them)
  const [showAllCheques, setShowAllCheques] = useState(false);

  // Search
  const [searchTerm, setSearchTerm] = useState("");

  // Filters
  const [projects, setProjects] = useState<{ id: number; name: string }[]>([]);
  // Editable filter inputs
  const [projectId, setProjectId] = useState<string>("");
  const [unitQuery, setUnitQuery] = useState("");
  const [paymentMethod, setPaymentMethod] = useState<string>("");
  const [receivedStart, setReceivedStart] = useState("");
  const [receivedEnd, setReceivedEnd] = useState("");
  const [instrumentStart, setInstrumentStart] = useState("");
  const [instrumentEnd, setInstrumentEnd] = useState("");
  const [amountMin, setAmountMin] = useState("");
  const [amountMax, setAmountMax] = useState("");
  // Applied filters (only used when Apply is clicked)
  const [appliedProjectId, setAppliedProjectId] = useState<string>("");
  const [appliedUnit, setAppliedUnit] = useState<string>("");
  const [appliedPaymentMethod, setAppliedPaymentMethod] = useState<string>("");
  const [appliedReceivedStart, setAppliedReceivedStart] = useState<string>("");
  const [appliedReceivedEnd, setAppliedReceivedEnd] = useState<string>("");
  const [appliedInstrumentStart, setAppliedInstrumentStart] =
    useState<string>("");
  const [appliedInstrumentEnd, setAppliedInstrumentEnd] = useState<string>("");
  const [appliedAmountMin, setAppliedAmountMin] = useState<string>("");
  const [appliedAmountMax, setAppliedAmountMax] = useState<string>("");

  // Debounced search effect to prevent input focus loss
  useEffect(() => {
    // load projects once
    projectService
      .getProjects(true)
      .then((ps) => {
        const projectList = ps.map((p) => ({ id: p.id, name: p.name }));
        setProjects(projectList);
      })
      .catch(() => setProjects([]));
  }, []);

  // Fetch when page changes or applied filters change
  useEffect(() => {
    fetchPendingIncomes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    currentPage,
    appliedProjectId,
    appliedUnit,
    appliedPaymentMethod,
    appliedReceivedStart,
    appliedReceivedEnd,
    appliedInstrumentStart,
    appliedInstrumentEnd,
    appliedAmountMin,
    appliedAmountMax,
    showAllCheques,
  ]);

  const fetchPendingIncomes = async () => {
    try {
      setLoading(true);
      const response = await incomeService.getPendingIncomes({
        page: currentPage,
        limit,
        projectId: appliedProjectId || undefined,
        paymentMethod: appliedPaymentMethod || undefined,
        receivedStart: appliedReceivedStart || undefined,
        receivedEnd: appliedReceivedEnd || undefined,
        instrumentStart: appliedInstrumentStart || undefined,
        instrumentEnd: appliedInstrumentEnd || undefined,
        unit: appliedUnit || undefined,
        amountMin: appliedAmountMin || undefined,
        amountMax: appliedAmountMax || undefined,
        showAllCheques,
      });
      setIncomes(response.incomes);
      setTotal(response.pagination.total);
      setTotalPages(response.pagination.totalPages);
    } catch (error) {
      console.error("Error fetching pending incomes:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectIncome = (incomeId: number) => {
    setSelectedIncomes((prev) =>
      prev.includes(incomeId)
        ? prev.filter((id) => id !== incomeId)
        : [...prev, incomeId]
    );
  };

  const handleSelectAll = () => {
    if (selectedIncomes.length === incomes.length) {
      setSelectedIncomes([]);
    } else {
      setSelectedIncomes(incomes.map((income) => income.id));
    }
  };

  const handleBulkApprove = () => {
    if (selectedIncomes.length > 0) {
      setShowApproveDialog(true);
    }
  };

  const confirmBulkApprove = async () => {
    setShowApproveDialog(false);
    try {
      await incomeService.approveIncomes(selectedIncomes);
      setSelectedIncomes([]);
      fetchPendingIncomes();
    } catch (error: any) {
      console.error("Error approving incomes:", error);
      alert(error.response?.data?.message || "Failed to approve incomes");
    }
  };

  const handleSingleApprove = (income: Income) => {
    setIncomeToApprove(income);
    setShowSingleApproveDialog(true);
  };

  const confirmSingleApprove = async () => {
    if (incomeToApprove) {
      setShowSingleApproveDialog(false);
      try {
        await incomeService.approveIncomes([incomeToApprove.id]);
        setSelectedIncomes([]);
        setIncomeToApprove(null);
        fetchPendingIncomes();
      } catch (error: any) {
        console.error("Error approving income:", error);
        alert(error.response?.data?.message || "Failed to approve income");
      }
    }
  };

  const handleRejectClick = (income: Income) => {
    setIncomeToReject(income);
    setRejectionReason("");
    setShowRejectDialog(true);
  };

  const confirmReject = async () => {
    if (incomeToReject && rejectionReason.trim()) {
      setShowRejectDialog(false);
      try {
        await incomeService.rejectIncome(
          incomeToReject.id,
          rejectionReason
        );
        setSelectedIncomes([]);
        setIncomeToReject(null);
        setRejectionReason("");
        fetchPendingIncomes();
      } catch (error: any) {
        console.error("Error rejecting income:", error);
        alert(error.response?.data?.message || "Failed to reject income");
      }
    }
  };

  const handleViewDetails = (income: Income) => {
    setSelectedIncome(income);
    setShowDetailsModal(true);
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };

  const handleApply = () => {
    setAppliedProjectId(projectId);
    setAppliedUnit(unitQuery);
    setAppliedPaymentMethod(paymentMethod);
    setAppliedReceivedStart(receivedStart);
    setAppliedReceivedEnd(receivedEnd);
    setAppliedInstrumentStart(instrumentStart);
    setAppliedInstrumentEnd(instrumentEnd);
    setAppliedAmountMin(amountMin);
    setAppliedAmountMax(amountMax);
    setCurrentPage(1);
  };

  const handleClearAll = () => {
    setProjectId("");
    setUnitQuery("");
    setPaymentMethod("");
    setReceivedStart("");
    setReceivedEnd("");
    setInstrumentStart("");
    setInstrumentEnd("");
    setAmountMin("");
    setAmountMax("");
    setSearchTerm("");
    setAppliedProjectId("");
    setAppliedUnit("");
    setAppliedPaymentMethod("");
    setAppliedReceivedStart("");
    setAppliedReceivedEnd("");
    setAppliedInstrumentStart("");
    setAppliedInstrumentEnd("");
    setAppliedAmountMin("");
    setAppliedAmountMax("");
    setCurrentPage(1);
  };

  if (loading && incomes.length === 0) {
    return (
      <div style={{ textAlign: "center", padding: "40px", color: "#6b7280" }}>
        Loading pending incomes...
      </div>
    );
  }

  return (
    <div className="page-container income-authorize-page" style={{ background: '#fff' }}>
      {/* Compact Action Bar */}
      <CompactActionBar
        leftActions={
          <>
            <button
              className={`filter-toggle-btn ${showFilters ? 'active' : ''}`}
              onClick={() => setShowFilters(!showFilters)}
            >
              <FaSearch /> Filters
            </button>

            <div className="cab-filter-group">
              <span>Project:</span>
              <select
                className="cab-select"
                value={projectId}
                onChange={(e) => {
                  setProjectId(e.target.value);
                  setAppliedProjectId(e.target.value);
                  setCurrentPage(1);
                }}
              >
                <option value="">All Projects</option>
                {projects.map((project) => (
                  <option key={project.id} value={project.id}>
                    {project.name}
                  </option>
                ))}
              </select>
            </div>
          </>
        }
        rightActions={
          <>
            {selectedIncomes.length > 0 && (
              <button 
                className="btn btn-success"
                onClick={handleBulkApprove}
                style={{ padding: '8px 16px', fontSize: '13px' }}
              >
                <FaCheck /> Approve ({selectedIncomes.length})
              </button>
            )}
          </>
        }
      />

      {/* Search Bar - Always Visible */}
      <div style={{ padding: '16px 20px', background: '#fff', borderBottom: '1px solid #e5e7eb' }}>
        <div style={{ maxWidth: '600px' }}>
          <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', color: '#6b7280', fontWeight: 500 }}>Search</label>
          <input
            type="text"
            placeholder="Customer, Booking ID, Unit, Reference Number..."
            value={searchTerm}
            onChange={handleSearchChange}
            className="form-input"
            style={{ margin: 0, width: '100%' }}
          />
        </div>
        <label
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: "8px",
            marginTop: "12px",
            fontSize: "13px",
            color: "#374151",
            cursor: "pointer",
          }}
        >
          <input
            type="checkbox"
            checked={showAllCheques}
            onChange={(e) => {
              setShowAllCheques(e.target.checked);
              setCurrentPage(1);
            }}
          />
          Show all cheques (include future-dated / post-dated)
        </label>
      </div>

      {/* Collapsible Filters */}
      {showFilters && (
        <div style={{ padding: '16px 20px', background: '#f9fafb', borderTop: '1px solid #e5e7eb' }} onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleApply(); } }}>
          <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-end', flexWrap: 'wrap' }}>
            <div style={{ minWidth: '140px' }}>
              <label style={{ display: 'block', marginBottom: '4px', fontSize: '13px', color: '#6b7280' }}>Unit</label>
              <input
                type="text"
                placeholder="e.g. G-401"
                className="form-input"
                value={unitQuery}
                onChange={(e) => setUnitQuery(e.target.value)}
                style={{ margin: 0 }}
              />
            </div>

            <div style={{ minWidth: '160px' }}>
              <label style={{ display: 'block', marginBottom: '4px', fontSize: '13px', color: '#6b7280' }}>Payment Method</label>
              <select
                className="form-select"
                value={paymentMethod}
                onChange={(e) => setPaymentMethod(e.target.value)}
                style={{ margin: 0 }}
              >
                <option value="">All Methods</option>
                {incomeService.getPaymentMethods().map((pm) => (
                  <option key={pm.value} value={pm.value}>
                    {pm.label}
                  </option>
                ))}
              </select>
            </div>

            <div style={{ minWidth: '280px' }}>
              <label style={{ display: 'block', marginBottom: '4px', fontSize: '13px', color: '#6b7280' }}>Received Date</label>
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                <input
                  type="date"
                  className="form-input"
                  value={receivedStart}
                  onChange={(e) => setReceivedStart(e.target.value)}
                  style={{ margin: 0 }}
                />
                <span style={{ fontSize: '13px', color: '#6b7280' }}>to</span>
                <input
                  type="date"
                  className="form-input"
                  value={receivedEnd}
                  onChange={(e) => setReceivedEnd(e.target.value)}
                  style={{ margin: 0 }}
                />
              </div>
            </div>

            <div style={{ minWidth: '280px' }}>
              <label style={{ display: 'block', marginBottom: '4px', fontSize: '13px', color: '#6b7280' }}>Instrument Date</label>
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                <input
                  type="date"
                  className="form-input"
                  value={instrumentStart}
                  onChange={(e) => setInstrumentStart(e.target.value)}
                  style={{ margin: 0 }}
                />
                <span style={{ fontSize: '13px', color: '#6b7280' }}>to</span>
                <input
                  type="date"
                  className="form-input"
                  value={instrumentEnd}
                  onChange={(e) => setInstrumentEnd(e.target.value)}
                  style={{ margin: 0 }}
                />
              </div>
            </div>

            <div style={{ minWidth: '200px' }}>
              <label style={{ display: 'block', marginBottom: '4px', fontSize: '13px', color: '#6b7280' }}>Amount Range</label>
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                <input
                  type="number"
                  placeholder="Min"
                  className="form-input"
                  value={amountMin}
                  onChange={(e) => setAmountMin(e.target.value)}
                  onWheel={(e) => e.currentTarget.blur()}
                  style={{ margin: 0, width: '90px' }}
                />
                <input
                  type="number"
                  placeholder="Max"
                  className="form-input"
                  value={amountMax}
                  onChange={(e) => setAmountMax(e.target.value)}
                  onWheel={(e) => e.currentTarget.blur()}
                  style={{ margin: 0, width: '90px' }}
                />
              </div>
            </div>

            <div style={{ display: 'flex', gap: '8px' }}>
              <button
                onClick={handleApply}
                className="btn btn-primary"
                style={{ padding: '8px 16px', fontSize: '13px' }}
              >
                Apply
              </button>
              <button
                onClick={handleClearAll}
                className="btn btn-secondary"
                style={{ padding: '8px 16px', fontSize: '13px' }}
              >
                Clear All
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Table Section */}
      <div style={{ background: '#fff', margin: 0, padding: '0 20px 20px' }}>
        {loading ? (
          <div style={{ textAlign: "center", padding: "40px", color: "#6b7280" }}>
            Loading incomes...
          </div>
        ) : incomes.length === 0 ? (
          <div style={{ textAlign: "center", padding: "60px 20px", color: "#6b7280" }}>
            <FaMoneyBillWave style={{ fontSize: "48px", color: "#d1d5db", marginBottom: "16px" }} />
            <h3 style={{ margin: "0 0 8px 0", color: "#374151", fontSize: "18px" }}>No Pending Incomes</h3>
            <p style={{ margin: 0, fontSize: "14px" }}>All incomes have been authorized</p>
          </div>
        ) : (
          <>
            <table className="standard-table" style={{ margin: 0 }}>
              <thead>
                <tr>
                  <th style={{ width: '80px', textAlign: 'center' }}>Sr. No</th>
                  <th>Project</th>
                  <th>Received Date</th>
                  <th>Unit No</th>
                  <th>Customer Name</th>
                  <th style={{ textAlign: 'right' }}>Amount</th>
                  <th>Payment Method</th>
                  <th>Instrument Date</th>
                  <th title="Days from today to the cheque instrument date (0 = today, -1 = yesterday, +1 = tomorrow)">Days</th>
                  <th>Ref Number</th>
                  <th>Bank Name</th>
                  <th style={{ width: '120px' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
            {incomes.filter(income => {
              // Client-side search filter
              if (!searchTerm.trim()) return true;
              
              const search = searchTerm.toLowerCase();
              const customerName = incomeService.getCustomerName(income).toLowerCase();
              const bookingId = income.booking?.bookingId?.toLowerCase() || '';
              const unitDisplay = incomeService.getUnitDisplay(income).toLowerCase();
              const referenceNumber = income.referenceNumber?.toLowerCase() || '';
              const bankName = income.bankName?.toLowerCase() || '';
              const projectName = income.booking?.project?.name?.toLowerCase() || '';
              const paymentMethod = income.paymentMethod?.toLowerCase() || '';
              
              return customerName.includes(search) || 
                     bookingId.includes(search) || 
                     unitDisplay.includes(search) || 
                     referenceNumber.includes(search) || 
                     bankName.includes(search) ||
                     projectName.includes(search) ||
                     paymentMethod.includes(search);
            }).map((income, index) => (
              <tr
                key={income.id}
                className={
                  selectedIncomes.includes(income.id) ? "selected" : ""
                }
              >
                <td style={{ fontWeight: '600', color: '#4f46e5', textAlign: 'center' }}>{index + 1}</td>
                <td><span className="project-name">{income.booking.project.name}</span></td>
                <td><span className="payment-date">{incomeService.formatDate(income.chequeReceivedDate || income.paymentDate)}</span></td>
                <td><span className="unit-number">{incomeService.getUnitDisplay(income)}</span></td>
                <td><div className="customer-info">{incomeService.getCustomerName(income)}</div></td>
                <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                  <span style={{ color: income.amount < 0 ? '#dc2626' : '#059669', fontWeight: 500 }}>
                    {incomeService.formatAmount(income.amount)}
                  </span>
                </td>
                <td><span className="payment-method">{incomeService.getPaymentMethodDisplay(income.paymentMethod)}</span></td>
                <td><span className="payment-date">{income.instrumentDate ? incomeService.formatDate(income.instrumentDate) : '-'}</span></td>
                <td style={{ textAlign: 'center', whiteSpace: 'nowrap' }}>{(() => {
                  if (income.paymentMethod !== 'cheque' || !income.instrumentDate) return <span style={{ color: '#9ca3af' }}>–</span>;
                  const d = new Date(income.instrumentDate);
                  const now = new Date();
                  const days = Math.round(
                    (Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()) -
                      Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate())) / 86400000
                  );
                  const color = days < 0 ? '#dc2626' : days === 0 ? '#059669' : '#6b7280';
                  const label = days > 0 ? `+${days}` : String(days);
                  return <span style={{ color, fontWeight: 600 }}>{label}</span>;
                })()}</td>
                <td><span className="reference">{income.referenceNumber || '-'}</span></td>
                <td><span className="bank-name">{income.bankName || '-'}</span></td>
                <td>
                  <div className="income-actions">
                    <button
                      className="btn-icon view"
                      onClick={() => handleViewDetails(income)}
                      title="View Details"
                    >
                      <FaEye />
                    </button>
                    <button
                      className="btn-icon approve"
                      onClick={() => handleSingleApprove(income)}
                      title="Approve"
                      style={{ fontSize: '18px' }}
                    >
                      <FaCheck />
                    </button>
                    <button
                      className="btn-icon reject"
                      onClick={() => handleRejectClick(income)}
                      title="Reject"
                    >
                      <FaTimes />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {/* Pagination */}
        {!loading && total > 0 && (
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            padding: '20px 0',
            borderTop: '1px solid #e5e7eb'
          }}>
            <div style={{ fontSize: '14px', color: '#6b7280' }}>
              Showing {((currentPage - 1) * limit) + 1} to {Math.min(currentPage * limit, total)} of {total} entries
            </div>
            <div style={{ display: 'flex', gap: '8px' }}>
              <button
                className="btn btn-secondary btn-sm"
                onClick={() => setCurrentPage(1)}
                disabled={currentPage === 1}
              >
                First
              </button>
              <button
                className="btn btn-secondary btn-sm"
                onClick={() => setCurrentPage(currentPage - 1)}
                disabled={currentPage === 1}
              >
                Previous
              </button>
              <span style={{ padding: '6px 12px', fontSize: '14px' }}>
                Page {currentPage} of {totalPages}
              </span>
              <button
                className="btn btn-secondary btn-sm"
                onClick={() => setCurrentPage(currentPage + 1)}
                disabled={currentPage === totalPages}
              >
                Next
              </button>
              <button
                className="btn btn-secondary btn-sm"
                onClick={() => setCurrentPage(totalPages)}
                disabled={currentPage === totalPages}
              >
                Last
              </button>
            </div>
          </div>
        )}
          </>
        )}
      </div>

      {/* Income Details Modal */}
      {showDetailsModal && selectedIncome && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h2>Income Details</h2>
              <button
                className="close-btn"
                onClick={() => setShowDetailsModal(false)}
              >
                ×
              </button>
            </div>

            <div className="modal-content">
              <div className="details-grid">
                <div className="detail-group">
                  <label>Booking ID:</label>
                  <span>{selectedIncome.booking.bookingId}</span>
                </div>
                <div className="detail-group">
                  <label>Customer:</label>
                  <span>{incomeService.getCustomerName(selectedIncome)}</span>
                </div>
                <div className="detail-group">
                  <label>Project:</label>
                  <span>{selectedIncome.booking.project.name}</span>
                </div>
                <div className="detail-group">
                  <label>Unit:</label>
                  <span>{incomeService.getUnitDisplay(selectedIncome)}</span>
                </div>
                <div className="detail-group">
                  <label>Amount:</label>
                  <span className="amount-large">
                    {incomeService.formatAmount(selectedIncome.amount)}
                  </span>
                </div>
                <div className="detail-group">
                  <label>Payment Date:</label>
                  <span>
                    {incomeService.formatDate(selectedIncome.paymentDate)}
                  </span>
                </div>
                <div className="detail-group">
                  <label>Instrument Date:</label>
                  <span>
                    {selectedIncome.instrumentDate
                      ? incomeService.formatDate(selectedIncome.instrumentDate)
                      : "N/A"}
                  </span>
                </div>
                <div className="detail-group">
                  <label>Payment Method:</label>
                  <span>
                    {incomeService.getPaymentMethodDisplay(
                      selectedIncome.paymentMethod
                    )}
                  </span>
                </div>
                <div className="detail-group">
                  <label>Reference Number:</label>
                  <span>{selectedIncome.referenceNumber || "N/A"}</span>
                </div>
                <div className="detail-group">
                  <label>Bank Name:</label>
                  <span>{selectedIncome.bankName || "N/A"}</span>
                </div>
                <div className="detail-group full-width">
                  <label>Description:</label>
                  <span>{selectedIncome.description || "N/A"}</span>
                </div>
                <div className="detail-group">
                  <label>Created:</label>
                  <span>
                    {incomeService.formatDate(selectedIncome.createdAt)}
                  </span>
                </div>
                <div className="detail-group">
                  <label>Created By:</label>
                  <span>{selectedIncome.createdBy || "N/A"}</span>
                </div>
              </div>

              <div className="modal-actions">
                <button
                  className="btn btn-success"
                  onClick={() => {
                    setShowDetailsModal(false);
                    handleSingleApprove(selectedIncome);
                  }}
                >
                  <FaCheck /> Approve
                </button>
                {/* HIDDEN: Reject Button - keeping code for future use */}
                {/* <button
                  className="btn btn-danger"
                  onClick={() => {
                    setShowDetailsModal(false);
                    handleRejectClick(selectedIncome);
                  }}
                >
                  <FaTimes /> Reject
                </button> */}
                <button
                  className="btn btn-secondary"
                  onClick={() => setShowDetailsModal(false)}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Approve Confirmation */}
      <ConfirmDialog
        isOpen={showApproveDialog}
        title="Approve Income Entries"
        message={`Are you sure you want to approve ${selectedIncomes.length} income entries? This action cannot be undone.`}
        onConfirm={confirmBulkApprove}
        onCancel={() => setShowApproveDialog(false)}
      />

      {/* Single Approve Confirmation */}
      <ConfirmDialog
        isOpen={showSingleApproveDialog}
        title="Approve Income Entry"
        message={incomeToApprove ? `Are you sure you want to approve this income of ${incomeService.formatAmount(incomeToApprove.amount)} from ${incomeService.getCustomerName(incomeToApprove)}?` : ""}
        onConfirm={confirmSingleApprove}
        onCancel={() => {
          setShowSingleApproveDialog(false);
          setIncomeToApprove(null);
        }}
      />

      {/* Reject Dialog */}
      {showRejectDialog && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h2>Reject Income Entry</h2>
              <button
                className="close-btn"
                onClick={() => setShowRejectDialog(false)}
              >
                ×
              </button>
            </div>

            <div className="modal-content">
              <p>
                You are about to reject the income entry of{" "}
                <strong>
                  {incomeToReject
                    ? incomeService.formatAmount(incomeToReject.amount)
                    : ""}
                </strong>
                .
              </p>

              <div className="form-group">
                <label htmlFor="rejectionReason">Rejection Reason *</label>
                <textarea
                  id="rejectionReason"
                  value={rejectionReason}
                  onChange={(e) => setRejectionReason(e.target.value)}
                  placeholder="Please provide a reason for rejection..."
                  rows={4}
                  required
                />
              </div>

              <div className="modal-actions">
                <button
                  className="btn btn-secondary"
                  onClick={() => setShowRejectDialog(false)}
                >
                  Cancel
                </button>
                <button
                  className="btn btn-danger"
                  onClick={confirmReject}
                  disabled={!rejectionReason.trim()}
                >
                  <FaTimes /> Reject
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default IncomeAuthorize;
